import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner, Alert, Badge, Table } from "react-bootstrap";
import { IconHistory, IconRotate, IconArchive } from "@tabler/icons-react";
import Swal from "sweetalert2";
import { api } from "../../../../api/Middleware";
import { CALENDAR_URL } from "../../../../api/Urls";

const STATUS_LABELS = {
  scheduled: "Agendado",
  canceled: "Cancelado",
  finished: "Concluído",
  rescheduled: "Remarcado",
  rescheduled_by_ai: "Remarcado (IA)",
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("pt-BR");
  } catch {
    return iso;
  }
};

const HistoryModal = ({ show, onHide, calendar }) => {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(30);

  useEffect(() => {
    if (!show || !calendar?.id) return;
    setLoading(true);
    api
      .get(`${CALENDAR_URL}${calendar.id}/history/?page=${page}&page_size=${pageSize}`)
      .then((resp) => {
        setEvents(resp.data?.events || []);
        setTotal(resp.data?.total || 0);
      })
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [show, calendar?.id, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Histórico — {calendar?.name}
          <Badge bg="secondary" className="ms-2">{total} evento(s)</Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" /> Carregando…
          </div>
        )}
        {!loading && events.length === 0 && (
          <Alert variant="info">Esta agenda não tem agendamentos no histórico.</Alert>
        )}
        {!loading && events.length > 0 && (
          <Table striped hover size="sm" responsive>
            <thead>
              <tr>
                <th>Data</th>
                <th>Título</th>
                <th>Cliente</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev) => (
                <tr key={ev.id}>
                  <td className="small">{formatDateTime(ev.start)}</td>
                  <td>{ev.title || "—"}</td>
                  <td className="small">{ev.client_phone || ev.client_email || "—"}</td>
                  <td>
                    <Badge bg={ev.status === "canceled" ? "danger" : "info"}>
                      {STATUS_LABELS[ev.status] || ev.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        {totalPages > 1 && (
          <div className="me-auto small text-muted">
            Página {page} de {totalPages}
            <Button
              size="sm"
              variant="link"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ‹ Anterior
            </Button>
            <Button
              size="sm"
              variant="link"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Próxima ›
            </Button>
          </div>
        )}
        <Button variant="secondary" onClick={onHide}>Fechar</Button>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * Modal de Agendas Arquivadas — lista todas com ações Reativar/Histórico.
 */
const ArchivedCalendarsModal = ({ show, onHide, canManage, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [historyCalendar, setHistoryCalendar] = useState(null);
  const [reactivating, setReactivating] = useState(null);

  const fetchArchived = async () => {
    setLoading(true);
    try {
      const resp = await api.get(`${CALENDAR_URL}archived/`);
      setItems(resp.data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show) fetchArchived();
  }, [show]);

  const handleReactivate = async (cal) => {
    const result = await Swal.fire({
      title: "Reativar agenda?",
      text: `A agenda "${cal.name}" voltará a aparecer no calendário e no agente, junto com os serviços e horários que tinha antes.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Reativar",
      cancelButtonText: "Cancelar",
    });
    if (!result.isConfirmed) return;
    setReactivating(cal.id);
    try {
      await api.post(`${CALENDAR_URL}${cal.id}/unarchive/`);
      // Reload da página após o usuário fechar o Swal — necessário pra
      // re-hidratar caches do contexto que carregam AppointmentTypes e
      // WorkingHours em mount (não há fetch granular pros tipos
      // reativados via cascade no backend).
      await Swal.fire("Reativada", `Agenda "${cal.name}" reativada com sucesso.`, "success");
      window.location.reload();
      return;
    } catch (err) {
      Swal.fire(
        "Erro",
        err.response?.data?.error || "Não foi possível reativar a agenda.",
        "error"
      );
    } finally {
      setReactivating(null);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <IconArchive size={20} className="me-2" />
            Agendas arquivadas
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading && (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" /> Carregando…
            </div>
          )}
          {!loading && items.length === 0 && (
            <Alert variant="info">Nenhuma agenda arquivada no momento.</Alert>
          )}
          {!loading && items.length > 0 && (
            <Table hover responsive size="sm">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Arquivada em</th>
                  <th>Por</th>
                  <th>Eventos</th>
                  <th className="text-end">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((cal) => (
                  <tr key={cal.id}>
                    <td><strong>{cal.name}</strong></td>
                    <td className="small">{formatDateTime(cal.archived_at)}</td>
                    <td className="small">{cal.archived_by || "—"}</td>
                    <td><Badge bg="secondary">{cal.event_count}</Badge></td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="me-2"
                        onClick={() => setHistoryCalendar(cal)}
                        title="Ver histórico de agendamentos"
                      >
                        <IconHistory size={14} /> Histórico
                      </Button>
                      {canManage && (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleReactivate(cal)}
                          disabled={reactivating === cal.id}
                          title="Reativar agenda"
                        >
                          {reactivating === cal.id ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <><IconRotate size={14} /> Reativar</>
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>Fechar</Button>
        </Modal.Footer>
      </Modal>

      <HistoryModal
        show={!!historyCalendar}
        onHide={() => setHistoryCalendar(null)}
        calendar={historyCalendar}
      />
    </>
  );
};

export default ArchivedCalendarsModal;
